"use client";

import { useMemo, useState, useEffect } from "react";

interface TocItem { id: string; text: string; level: number; }

function extractHeadings(markdown: string): TocItem[] {
  const lines = markdown.split("\n");
  const headings: TocItem[] = [];
  for (const line of lines) {
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].replace(/[*_`\[\]]/g, "").trim();
      const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
      headings.push({ id, text, level });
    }
  }
  return headings;
}

function getIndent(level: number, minLevel: number): string {
  const depth = level - minLevel;
  if (depth <= 0) return "12px";
  if (depth === 1) return "28px";
  if (depth === 2) return "40px";
  return "52px";
}

export default function TableOfContents({ content, className }: { content: string; className?: string }) {
  const headings = useMemo(() => extractHeadings(content), [content]);
  const minLevel = useMemo(() => headings.length > 0 ? Math.min(...headings.map(h => h.level)) : 1, [headings]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const sorted = visible.sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          );
          setActiveId(sorted[0].target.id);
        }
      },
      {
        rootMargin: "-80px 0px -70% 0px",
        threshold: 0,
      }
    );

    const elements: Element[] = [];
    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (el) {
        observer.observe(el);
        elements.push(el);
      }
    }

    return () => {
      for (const el of elements) {
        observer.unobserve(el);
      }
    };
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav aria-label="Table of contents" className={className || ""}>
      <h3 className="font-semibold uppercase text-[0.6875rem] tracking-widest text-[--muted] mb-4">
        Contents
      </h3>
      <div className="h-px bg-[--border] mb-3.5" />
      <ul className="flex flex-col gap-0.5 list-none p-0 m-0">
        {headings.map((heading, i) => {
          const isActive = activeId === heading.id;
          return (
            <li key={i}>
              <a
                href={`#${heading.id}`}
                className="block no-underline transition-colors"
                style={{
                  paddingLeft: getIndent(heading.level, minLevel),
                  paddingTop: "6px",
                  paddingBottom: "6px",
                  fontWeight: isActive ? 600 : (heading.level <= minLevel ? 500 : 400),
                  fontSize: heading.level <= minLevel ? "0.8125rem" : "0.75rem",
                  lineHeight: "1.4",
                  color: isActive ? "var(--link)" : "var(--muted)",
                  borderLeft: isActive
                    ? "2px solid var(--link)"
                    : "2px solid transparent",
                }}
                onClick={() => {
                  setActiveId(heading.id);
                }}
              >
                {heading.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
